import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause } from 'lucide-react';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';
import { timelineEpochMs } from '../../../utils/gdacs/stormClock';
import { formatTimeDisplayUtc, generateDayMarkers } from '../../../utils/gdacs/timelineFormat';

/** Default timeline range when no caller-provided cap is available. */
const DEFAULT_TIMELINE_HOURS = 240;

const TRACK_STYLE = {
  background: '#ffffff',
  border: '1.5px solid rgba(0,0,0,0.55)',
  thumbGlow: '#aaaaaa',
};

/**
 * Speed options for timeline playback
 * Values are milliseconds between frames - lower = faster animation
 * Slower speeds give tiles time to load and be visible
 */
const SPEED_OPTIONS = [
  { label: '0.5x', value: 3000 },  // Slowest - 3 seconds per frame
  { label: '1x', value: 1500 },    // Default - 1.5 seconds per frame
  { label: '2x', value: 800 },     // Faster - 0.8 seconds per frame
];

/**
 * WeatherMapTimeline - Timeline controls for weather animation
 *
 * Features:
 * - Play/pause button
 * - Time display showing current forecast time
 * - Slider to scrub through time (0-240 hours = 10 days)
 * - Speed controls (0.5x, 1x, 2x)
 * - Day markers for quick navigation
 * - Disabled state for free tier (Maps 1.0)
 *
 * API Tier Notes:
 * - Maps 1.0 (free): Current weather only - timeline disabled
 * - Maps 2.0 (~$40/mo): 10-day forecast with time parameter
 */
const WeatherMapTimeline = ({
  currentHour = 0,
  isPlaying = false,
  isLoadingTiles = false, // True when paused for zoom/tile loading
  playSpeed = 200,
  onPlayPause,
  onTimeChange,
  onSpeedChange,
  startDate = new Date(),
  apiMode = 'free', // 'free' or 'paid'
  maxHours,
  step,
  // `card` is the Global Produce Map treatment (white surface, dark playhead
  // pill, green track). `strip` is the original home WeatherMap bar.
  variant = 'strip',
}) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();

  // Effective timeline range. Caller can pass a smaller value when the API's
  // forecast horizon ends before the default 240h (e.g. user mounted late in
  // the OWM refresh cycle).
  const timelineHours = Math.max(0, maxHours ?? DEFAULT_TIMELINE_HOURS);
  const sliderStep = Math.max(1, step ?? (apiMode === 'paid' ? 3 : 1));
  const numDays = Math.max(0, Math.floor(timelineHours / 24));

  // Calculate the current display date/time
  const currentDate = useMemo(() => {
    return new Date(timelineEpochMs(startDate, currentHour));
  }, [startDate, currentHour]);

  // Generate day markers — exactly enough to span the actual horizon.
  const dayMarkers = useMemo(
    () => generateDayMarkers(startDate, numDays),
    [startDate, numDays],
  );

  const playheadGlowStyle = {
    boxShadow: `0 0 0 1.5px rgba(0,0,0,0.55), 0 0 8px ${TRACK_STYLE.thumbGlow}80`,
  };

  // Calculate slider fill percentage
  const fillPercentage = timelineHours > 0 ? (currentHour / timelineHours) * 100 : 0;

  // Determine which day is active
  const activeDay = Math.floor(currentHour / 24);

  // Disabled state for free tier
  const isDisabled = apiMode === 'free';

  // Last marker reads "+9d" rather than a date, so the horizon is legible at a
  // glance without a separate end label flanking the slider.
  const markerLabel = (marker) => {
    if (marker.isToday) return 'Today';
    if (marker.index === numDays && numDays > 0) return `+${numDays}d`;
    return marker.label;
  };

  if (variant === 'card') {
    return (
      <div
        className={`border-t border-[#E8EBE9] bg-white px-[10px] py-[8px] dark:border-[#30363d] dark:bg-[#1d1d1d] lg:px-[16px] ${
          isDisabled ? 'opacity-60' : ''
        }`}
      >
        {/* One row instead of three (spec: "taking less height now") — the
            playhead sits beside the track rather than above it. Slider and day
            markers share a single flex column so the labels stay aligned with
            the positions they scrub to. Below lg the column drops to its own
            line (`order-2 w-full`) with the speed buttons pushed to the right
            of the first line (`order-1 ml-auto`); from lg everything sits on
            one line in DOM order. */}
        <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[6px]">
          <div className="flex items-center gap-[10px]">
            <button
              type="button"
              onClick={onPlayPause}
              disabled={isDisabled}
              className={`flex h-[28px] w-[28px] shrink-0 cursor-pointer items-center justify-center rounded-full text-white transition-colors ${
                isPlaying && isLoadingTiles ? 'bg-[#f59e0b]' : 'bg-bloom-green hover:bg-bloom-green-hover'
              } disabled:cursor-not-allowed disabled:bg-[#C3C4C3]`}
              aria-label={
                isPlaying
                  ? t('home.weatherMap.timeline.pause', 'Pause')
                  : t('home.weatherMap.timeline.play', 'Play')
              }
            >
              {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-[1px]" />}
            </button>

            <div className="flex items-center gap-[6px] rounded-[6px] bg-[#282A29] px-[10px] py-[5px] dark:bg-[#0f0f0f]">
              <span className="font-['Inter'] whitespace-nowrap text-[12px] font-semibold leading-[18px] text-white">
                {formatTimeDisplayUtc(currentDate)}
              </span>
              <span className="font-['Inter'] text-[11px] leading-[16px] text-[#A8ADA9]">UTC</span>
            </div>

            {isLoadingTiles && (
              <span className="animate-pulse whitespace-nowrap rounded bg-[#f59e0b]/20 px-2 py-0.5 text-[11px] text-[#f59e0b]">
                Loading...
              </span>
            )}
          </div>

          {/* Track + day markers */}
          <div className="order-2 w-full min-w-0 lg:order-none lg:w-auto lg:flex-1">
            <div className="relative h-[14px] touch-pan-x">
              <div className="absolute left-0 right-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-[#E8EBE9] dark:bg-[#30363d]" />
              <div
                className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-bloom-green"
                style={{ width: `${fillPercentage}%` }}
              />

              <input
                type="range"
                min="0"
                max={timelineHours}
                step={sliderStep}
                value={currentHour}
                onChange={(e) => onTimeChange(parseInt(e.target.value, 10))}
                disabled={isDisabled}
                className="absolute left-0 top-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                style={{ WebkitAppearance: 'none' }}
              />

              <div
                className="pointer-events-none absolute top-1/2 h-[12px] w-[12px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-bloom-green shadow-[0_1px_3px_rgba(0,0,0,0.2)]"
                style={{ left: `${fillPercentage}%` }}
              />
            </div>

            <div className="scrollbar-hide overflow-x-auto">
              <div className="flex min-w-[500px] justify-between sm:min-w-0">
                {dayMarkers.map((marker) => {
                  const isActive = activeDay === marker.index;
                  return (
                    <button
                      key={marker.index}
                      type="button"
                      onClick={() => onTimeChange(marker.hour)}
                      disabled={isDisabled}
                      className={`font-['Inter'] cursor-pointer whitespace-nowrap rounded px-[4px] text-[11px] leading-[16px] transition-colors ${
                        isActive
                          ? 'font-semibold text-bloom-green'
                          : 'text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2]'
                      } disabled:cursor-not-allowed`}
                    >
                      {markerLabel(marker)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="order-1 ml-auto flex items-center gap-[6px] lg:order-none lg:ml-0">
            {SPEED_OPTIONS.map((option) => {
              const isActive = playSpeed === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSpeedChange(option.value)}
                  disabled={isDisabled}
                  className={`font-['Inter'] h-[24px] cursor-pointer rounded-[6px] px-[10px] text-[11px] font-medium transition-colors ${
                    isActive
                      ? 'bg-bloom-green text-white'
                      : 'border border-[#E8EBE9] bg-white text-[#5D625F] hover:border-bloom-green/60 dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#828382]'
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`px-2 sm:px-3 py-2 border-t border-[#E8EBE9] dark:border-[#3a3c3b] bg-[#F0F5F2] dark:bg-[#232121] ${
        isDisabled ? 'opacity-60' : ''
      }`}
    >
      {/* Top row: Play button, time display, info, speed controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        {/* Left: Play button and time display */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onPlayPause}
            disabled={isDisabled}
            className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
              isPlaying
                ? isLoadingTiles
                  ? 'bg-[#f59e0b] text-white' // Orange when loading tiles during play
                  : 'bg-[#3b82f6] text-white'
                : 'bg-bloom-green text-white hover:bg-bloom-green-hover'
            } disabled:bg-[#C3C4C3] disabled:cursor-not-allowed`}
            aria-label={
              isPlaying
                ? t('home.weatherMap.timeline.pause', 'Pause')
                : t('home.weatherMap.timeline.play', 'Play')
            }
          >
            {isPlaying ? (
              <Pause size={14} className="sm:w-4 sm:h-4" />
            ) : (
              <Play size={14} className="ml-0.5 sm:w-4 sm:h-4" />
            )}
          </button>

          <div
            className="px-2 sm:px-3 py-1 rounded-md bg-bloom-green text-white font-semibold whitespace-nowrap"
            style={{ fontSize: fontSize - 3 }}
          >
            {formatTimeDisplayUtc(currentDate, '-')}
          </div>

          {/* API mode indicator - hidden on very small screens */}
          <span
            className={`hidden xs:inline px-2 py-0.5 rounded text-xs whitespace-nowrap ${
              isDisabled
                ? 'bg-[#ff6b6b]/20 text-[#ff6b6b]'
                : 'bg-bloom-green/20 text-bloom-green'
            }`}
            style={{ fontSize: fontSize - 4 }}
          >
            {isDisabled
              ? 'Maps 1.0'
              : 'Maps 2.0'}
          </span>

          {/* Loading indicator while tiles are loading */}
          {isLoadingTiles && (
            <span
              className="px-2 py-0.5 rounded text-xs whitespace-nowrap bg-[#f59e0b]/20 text-[#f59e0b] animate-pulse"
              style={{ fontSize: fontSize - 4 }}
            >
              Loading...
            </span>
          )}
        </div>

        {/* Right: Speed controls */}
        <div className="flex items-center gap-1">
          {SPEED_OPTIONS.map((option) => {
            const isActive = playSpeed === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onSpeedChange(option.value)}
                disabled={isDisabled}
                className={`px-1.5 sm:px-2 py-0.5 rounded text-xs transition-colors ${
                  isActive
                    ? 'bg-bloom-green text-white'
                    : 'bg-white dark:bg-[#191919] border border-[#C3C4C3] dark:border-[#828382] text-[#828382]'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{ fontSize: fontSize - 4 }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Slider row */}
      <div className="flex items-center gap-2 mb-5">
        <span className="text-[#828382] min-w-[28px] sm:min-w-[32px]" style={{ fontSize: fontSize - 4 }}>
          {t('home.weatherMap.timeline.now', 'Now')}
        </span>

        <div className="flex-1 relative h-6 touch-pan-x">
          <div
            className="absolute top-1/2 left-0 right-0 h-2 sm:h-1.5 rounded-full -translate-y-1/2"
            style={{
              background: TRACK_STYLE.background,
              border: TRACK_STYLE.border,
            }}
          />

          {/* Slider input - larger touch target on mobile */}
          <input
            type="range"
            min="0"
            max={timelineHours}
            step={sliderStep}
            value={currentHour}
            onChange={(e) => onTimeChange(parseInt(e.target.value, 10))}
            disabled={isDisabled}
            className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            style={{ WebkitAppearance: 'none' }}
          />

          {/* Thumb — centered on track, smooth glow transition between segments */}
          <div
            className="absolute top-1/2 w-4 h-4 sm:w-3 sm:h-3 bg-white border-2 border-white rounded-full -translate-y-1/2 -translate-x-1/2 pointer-events-none transition-shadow duration-500"
            style={{ left: `${fillPercentage}%`, ...playheadGlowStyle }}
          />
        </div>

        <span className="text-[#828382] min-w-[28px] sm:min-w-[32px] text-right" style={{ fontSize: fontSize - 4 }}>
          {`+${numDays}d`}
        </span>
      </div>

      {/* Day markers - scrollable on mobile */}
      <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0">
        <div className="flex justify-between min-w-[500px] sm:min-w-0 px-4 sm:px-8">
          {dayMarkers.map((marker) => {
            const isActive = activeDay === marker.index;
            return (
              <button
                key={marker.index}
                type="button"
                onClick={() => onTimeChange(marker.hour)}
                disabled={isDisabled}
                className={`text-xs px-1 py-0.5 rounded transition-colors whitespace-nowrap ${
                  isActive
                    ? 'text-bloom-green font-semibold'
                    : 'text-[#828382] hover:bg-[#E8EBE9] dark:hover:bg-[#3a3c3b]'
                } disabled:cursor-not-allowed`}
                style={{ fontSize: fontSize - 4 }}
              >
                {marker.isToday ? 'Today' : marker.label}
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default WeatherMapTimeline;
